import { type MetadataCsv, useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { error as errorLog, warn } from '@/utilities/log';
import { capitalizeFileExtension } from '@/lib/utils';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { type FieldType } from '@/types/metadata';
import { useTranslations } from 'next-intl';

/** The minimal shape a CSV column can match against — the tenant's metadata/workflow field
 * lists and a contributor's resolved-link lists both map into this. */
export type CsvMetadataFieldOption = { slug: string; type: FieldType };
export type CsvWorkflowOption = { slug: string; statusSlugs: Array<string> };

/** Parses a metadata CSV into the upload session's csv/manual-metadata state. Shared by the
 * tenant uploader and the contributor submission uploader so column semantics and conflict
 * handling never drift between the two. */
export const useCsvMetadata = (
  sessionId: string,
  workflowOptions: Array<CsvWorkflowOption>,
  metadataDefinitions: Array<CsvMetadataFieldOption>,
) => {
  const tErrors = useTranslations('errors');
  const tUpload = useTranslations('upload');
  const { toast } = useToast();
  const openModal = useOpenModal();
  const setCsvFileName = useUploadStore((state) => state.setCsvFileName);
  const setCsvData = useUploadStore((state) => state.setCsvData);
  const setManualMetadata = useUploadStore((state) => state.setManualMetadata);

  const processCsvFile = async (file: File) => {
    const session = useUploadStore.getState().uploads[sessionId];
    if (!session) {
      return;
    }
    const { csvData, manualMetadata } = session;
    const originalCsvData = { ...csvData };

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'csv') {
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tUpload('invalidCsvDataTitle'),
        description: tUpload('invalidCsvFileType'),
      });
      return;
    }

    setCsvFileName(sessionId, file.name);
    const { readString } = await import('react-papaparse');

    const reader = new FileReader();
    reader.onload = () => {
      const csvContent = reader.result as string;

      readString(csvContent, {
        header: true,
        skipEmptyLines: true,
        complete: (results: { data: Array<MetadataCsv>; errors: Array<object>; meta: object }) => {
          try {
            const metadataObject = results.data.reduce<Record<string, MetadataCsv>>((acc, item) => {
              const metadata: Record<string, any> = {};
              const workflows: Record<string, any> = {};
              const otherFields: Record<string, any> = {};
              let tags: Array<string> = [];

              Object.entries(item).forEach(([key, value]) => {
                if (key.startsWith('metadata.')) {
                  const newKey = key.replace('metadata.', '');
                  const field = metadataDefinitions.find((f) => f.slug === newKey);

                  if (field) {
                    let parsedVal: any = value;

                    if (field.type === 'boolean') {
                      parsedVal = ['1', 'true', 'yes', 'si', 'oui'].includes(value as string) ? true : false;
                    } else if (field.type === 'multi_select') {
                      parsedVal = value === '' ? [] : JSON.parse((value as string).replace(/([^[\],]+)/g, '"$1"'));
                    }
                    metadata[newKey] = parsedVal;
                  }
                } else if (key.startsWith('workflow.')) {
                  const newKey = key.replace('workflow.', '');

                  const workflowDef = workflowOptions.find((wf) => wf.slug === newKey);

                  if (workflowDef) {
                    const isValidStatus = workflowDef.statusSlugs.includes(value as string);

                    if (isValidStatus) {
                      workflows[newKey] = value;
                    }
                  }
                } else if (key === 'tags') {
                  tags = JSON.parse((value as string).replace(/([^[\],]+)/g, '"$1"'));
                } else if (key === 'filename') {
                  otherFields['filename'] = capitalizeFileExtension(value as string);
                } else {
                  otherFields[key] = value;
                }
              });

              const fileKey = otherFields.filename
                ? otherFields.filename.startsWith('./')
                  ? otherFields.filename.slice(2)
                  : otherFields.filename.startsWith('/')
                    ? otherFields.filename.slice(1)
                    : otherFields.filename
                : null;

              acc[fileKey] = { ...otherFields, metadata, workflows, tags } as MetadataCsv;
              return acc;
            }, {});

            setCsvData(sessionId, metadataObject);

            let conflictingValues = false;

            for (const fileName in metadataObject) {
              const csvEntry = metadataObject[fileName];
              if (!csvEntry) {
                continue;
              }

              const csvMeta = csvEntry.metadata as Record<string, any>;
              const csvDescription = csvEntry.description;
              const csvRating = csvEntry.rating;
              const csvTags = csvEntry.tags;
              const csvWorkflows = csvEntry.workflows as Record<string, any>;

              // Find matching manualMetadata entry by csvDataKeyRecognition
              const matchingEntry = Object.entries(manualMetadata).find(
                ([, value]) =>
                  (value.metadata as { csvDataKeyRecognition?: string })?.csvDataKeyRecognition ===
                  capitalizeFileExtension(fileName),
              );
              if (!matchingEntry) {
                continue;
              }

              const [, manualMeta] = matchingEntry;
              const manualCsvMetadata = manualMeta.metadata as Record<string, any>;
              const manualDescription = manualMeta.description;
              const manualRating = manualMeta.rating;
              const manualTags = manualMeta.tags;
              const manualWorkflows = manualMeta.workflows as Record<string, any>;

              // ---- Metadata ----
              for (const key in csvMeta) {
                if (key === 'csvDataKeyRecognition') {
                  continue;
                }

                const csvValue = csvMeta[key];
                const manualValue = manualCsvMetadata[key] ?? undefined;

                if (manualValue === undefined) {
                  continue;
                }

                const valuesAreDifferent =
                  Array.isArray(csvValue) && Array.isArray(manualValue)
                    ? JSON.stringify(csvValue) !== JSON.stringify(manualValue)
                    : csvValue !== manualValue;

                if (valuesAreDifferent) {
                  conflictingValues = true;
                  break;
                }
              }
              if (conflictingValues) {
                break;
              }

              // ---- Description ----
              if (
                manualDescription !== undefined &&
                csvDescription !== undefined &&
                manualDescription !== csvDescription
              ) {
                conflictingValues = true;
                break;
              }

              // ---- Rating ----
              if (manualRating !== undefined && csvRating !== undefined && manualRating !== csvRating) {
                conflictingValues = true;
                break;
              }

              // ---- Tags ----
              if (Array.isArray(manualTags) && Array.isArray(csvTags)) {
                const tagsDifferent = JSON.stringify(manualTags) !== JSON.stringify(csvTags);
                if (tagsDifferent) {
                  conflictingValues = true;
                  break;
                }
              }

              // ---- Workflows ----
              if (manualWorkflows && csvWorkflows) {
                const workflowsDifferent = JSON.stringify(manualWorkflows) !== JSON.stringify(csvWorkflows);
                if (workflowsDifferent) {
                  conflictingValues = true;
                  break;
                }
              }
            }
            if (conflictingValues) {
              openModal(ModalId.Csv, {
                text: tUpload('overwriteMetadataConfirmation'),
                overwrite: () => {
                  for (const fileName in metadataObject) {
                    const csvEntry = metadataObject[fileName];
                    if (!csvEntry) {
                      continue;
                    }

                    const csvMetadata = (csvEntry.metadata ?? {}) as Record<string, any>;
                    const csvWorkflows = (csvEntry.workflows ?? {}) as Record<string, any>;

                    // find manual entry by recognition key
                    const matchingEntry = Object.entries(manualMetadata).find(
                      ([, value]) =>
                        (value.metadata as { csvDataKeyRecognition?: string })?.csvDataKeyRecognition ===
                        capitalizeFileExtension(fileName),
                    );
                    if (!matchingEntry) {
                      continue;
                    }

                    const [fileId, manualFileMetadata] = matchingEntry;

                    // ---- METADATA: keep only manual keys NOT present in CSV ----
                    const manualMeta = (manualFileMetadata.metadata ?? {}) as Record<string, any>;
                    const updatedMetadata: Record<string, any> = {};
                    for (const key in manualMeta) {
                      if (key === 'csvDataKeyRecognition') {
                        continue;
                      }
                      if (!(key in csvMetadata)) {
                        updatedMetadata[key] = manualMeta[key];
                      }
                    }
                    // recognition stays
                    updatedMetadata.csvDataKeyRecognition = fileName;

                    // ---- WORKFLOWS: same rule as metadata (key-level clear) ----
                    const manualWorkflows = (manualFileMetadata.workflows ?? {}) as Record<string, any>;
                    let updatedWorkflows: Record<string, any> | undefined;
                    if (csvEntry.workflows !== undefined) {
                      updatedWorkflows = {};
                      for (const key in manualWorkflows) {
                        if (!(key in csvWorkflows)) {
                          updatedWorkflows[key] = manualWorkflows[key];
                        }
                      }
                    }
                    // If CSV didn't provide workflows at all, we don't touch workflows.

                    // Build a presence-driven patch for setManualMetadata overwrite mode
                    const patch: Partial<MetadataCsv> = {
                      metadata: updatedMetadata,
                      // If CSV provides description/rating/tags, *clear* them.
                      ...(csvEntry.description !== undefined ? { description: undefined } : {}),
                      ...(csvEntry.rating !== undefined ? { rating: undefined } : {}),
                      ...(csvEntry.tags !== undefined ? { tags: undefined } : {}),
                      ...(csvEntry.workflows !== undefined ? { workflows: updatedWorkflows } : {}),
                    };

                    setManualMetadata(
                      sessionId,
                      patch,
                      [{ id: fileId, name: fileName }],
                      false, // overwrite mode: presence-driven set/clear
                    );
                  }
                },
                keep: () => {},
                originalCsvData,
              });
            }
          } catch (err: any) {
            errorLog('Invalid CSV Data:', err);
            toast({
              variant: ToastVariant.DESTRUCTIVE,
              title: tUpload('invalidCsvDataTitle'),
              description: err.message ?? tErrors('toastDefaultDescription'),
            });
          }
        },
      });
    };

    reader.onerror = () => warn('Error reading file');
    reader.readAsText(file);
  };

  return { processCsvFile };
};
